/**
 * /earthworks/jobs/:id/log/:workDate — the office writes or corrects a job's
 * daily log for a day (fieldiq-api#1284). A full page.
 *
 * It is the SAME form the crew uses on the phone (EwLogForm) over the same
 * reads and the same day upsert (GET / PUT /earthworks/field/jobs/:id/
 * daily-logs/:workDate), which let an office role through whether or not they
 * are on the crew. One form, one body shape, so the office cannot write a log
 * the phone would read differently. SNAKE_CASE both ways, like the phone.
 *
 * Pre-use checks are filed on the phone; here an existing one can be linked but
 * not filed. Warnings show with a "Carry on" each and never disable Save — an
 * office carry-on is recorded under the office user's name. A future day is
 * refused by the API (future_day) and said as a sentence. Saved, it goes to the
 * log's own page.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useEwMeta } from '../../hooks/useEarthworks';
import { useEwFieldJob, useEwFieldLog, useEwPreUseChecks, useSaveEwFieldLog } from '../../hooks/useEwField';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchEw, ewApiError, ewDay, ewMeterWarningText, ewRefusal } from '../../lib/earthworks';
import type { EwFieldLogSaved } from '../../types/earthworks';
import { useBackToList } from '../downhole/recordPage';
import { EW_EYEBROW, EwEmpty, EwLoading, EwPanel } from './EwBits';
import { fromFieldWarning } from './EwWarnings';
import { EwLogForm } from './field/EwLogForm';

export default function EwOfficeLogEditScreen() {
  const { id, workDate } = useParams<{ id: string; workDate: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const jobQuery = useEwFieldJob(id);
  const logQuery = useEwFieldLog(id, workDate);
  const checks = useEwPreUseChecks(id, workDate);
  const catalog = useEwMeta().data?.data ?? null;
  const save = useSaveEwFieldLog();
  const [error, setError] = useState<string | null>(null);
  const [meterWarnings, setMeterWarnings] = useState<EwFieldLogSaved['warnings']>([]);
  const back = useBackToList(id ? `/earthworks/jobs/${id}?tab=logs` : '/earthworks/jobs');

  const job = jobQuery.data?.data ?? null;
  const log = logQuery.data?.data ?? null;
  const meta = logQuery.data?.meta ?? jobQuery.data?.meta ?? null;
  const warnings = (logQuery.data?.dayWarnings ?? []).map(fromFieldWarning);
  const loggable = job?.status === 'scheduled' || job?.status === 'in_progress';
  const canWrite = canDispatchEw(user?.role) && loggable;
  const failure = queryLoadFailure(jobQuery) ?? queryLoadFailure(logQuery);
  const unitName = (equipmentId?: string) => job?.equipment.find((e) => e.equipment_id === equipmentId)?.equipment_name ?? null;

  return (
    <div data-testid="ew-office-log-edit" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job?.job_number ? `${EW_EYEBROW} · ${job.job_number}` : EW_EYEBROW}
        title={`${log ? 'Correct' : 'Write'} daily log · ${ewDay(workDate)}`}
        onBack={back}
        backLabel="Back"
        stats={[
          { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: 'Work date', tone: 'neutral', loading: false, value: ewDay(workDate) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {jobQuery.isLoading || logQuery.isLoading ? (
            <EwLoading label="Loading daily log" />
          ) : failure ? (
            <EwPanel eyebrow="Daily log" title="Daily log">
              <LoadFailureNotice error={queryFailureError(jobQuery) ?? queryFailureError(logQuery)} kind={failure} subject="this daily log" />
            </EwPanel>
          ) : !job ? (
            <EwPanel eyebrow="Daily log" title="Daily log">
              <EwEmpty>That job no longer exists. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : (
            <>
              {!loggable && (
                <p role="status" className="rounded-fi-control border border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  {job.status === 'draft'
                    ? 'This job is still a draft. Schedule it before work is logged.'
                    : 'This job is completed or cancelled. Its logs can no longer be changed.'}
                </p>
              )}
              {meterWarnings.length > 0 && (
                <div role="status" data-testid="ew-office-meter-warnings" className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
                  <ul className="space-y-1">
                    {meterWarnings.map((w, i) => (
                      <li key={i}>{ewMeterWarningText(w, unitName(w.equipment_id))}</li>
                    ))}
                  </ul>
                </div>
              )}
              <EwPanel eyebrow="Daily log" title={`${job.customer_name || '—'} · ${job.site_name || '—'}`} testId="ew-office-log-form">
                <div className="px-4 py-4">
                  <EwLogForm
                    key={log?.id ?? 'new'}
                    jobId={job.id}
                    workDate={workDate ?? ''}
                    job={job}
                    log={log}
                    catalog={catalog}
                    meta={meta}
                    checks={checks.data?.data ?? []}
                    warnings={warnings}
                    userId={user?.id ?? null}
                    pending={save.isPending}
                    error={error}
                    readOnly={!canWrite}
                    submitLabel={log ? 'Save corrections' : 'Save log'}
                    onSubmit={(body) => {
                      setError(null);
                      save.mutate(
                        { jobId: job.id, day: workDate as string, body },
                        {
                          onSuccess: (res) => {
                            toast.success('Daily log saved');
                            if (res.warnings.length > 0) {
                              setMeterWarnings(res.warnings);
                              return;
                            }
                            if (res.data?.id) navigate(`/earthworks/daily-logs/${res.data.id}`, { replace: true });
                          },
                          onError: (err) =>
                            setError(
                              ewApiError(err).status === null
                                ? 'The daily log was NOT saved — no answer from the server. Try again.'
                                : ewRefusal(err, 'The daily log could not be saved.'),
                            ),
                        },
                      );
                    }}
                  />
                </div>
              </EwPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
